import {
  DefinitionModule,
  type ModuleDefinition,
  type ModuleRarity,
  type ProjectileModule,
} from "./ProjectileModule.js";
import type { ModuleEffectData } from "./ModuleEffect.js";
import type { ModuleSlotType } from "../models/ModuleTypes.js";
import { ALL_MODULE_DEFINITIONS } from "../data/moduleDefinitions.js";

/** 모듈 정의 조회 + 인스턴스 생성. 상자/슬롯/인벤토리가 공유하는 카탈로그 게이트웨이. */
export class ModuleRegistry {
  private readonly byId = new Map<string, ModuleDefinition>();

  constructor(defs: readonly ModuleDefinition[] = ALL_MODULE_DEFINITIONS) {
    for (const d of defs) this.byId.set(d.id, d);
  }

  has(id: string): boolean {
    return this.byId.has(id);
  }

  getDefinition(id: string): ModuleDefinition | undefined {
    return this.byId.get(id);
  }

  allIds(): string[] {
    return [...this.byId.keys()];
  }

  idsBySlot(slot: ModuleSlotType): string[] {
    return [...this.byId.values()].filter((d) => d.slotType === slot).map((d) => d.id);
  }

  slotOf(id: string): ModuleSlotType | undefined {
    return this.byId.get(id)?.slotType;
  }

  /**
   * 정의 + 레벨 → 런타임 모듈. 알 수 없는 id면 throw (호출자가 has로 가드).
   * rarity 주입 시 등급별 effect가 적용된다 — 합성 오버라이드 반영용(effectiveRarity 전달).
   * 미지정 시 정의 등급(미합성 모듈). masteryEffects는 해금된 숙련도 노드 효과(게이트 없이 항상 적용).
   */
  create(
    id: string,
    level: number,
    rarity?: ModuleRarity,
    masteryEffects: readonly ModuleEffectData[] = [],
  ): ProjectileModule {
    const def = this.byId.get(id);
    if (!def) throw new Error(`[ModuleRegistry] unknown module id: ${id}`);
    return new DefinitionModule(def, Math.max(1, Math.floor(level)), rarity ?? def.rarity, masteryEffects);
  }
}
